import { BandgeStatus } from "components/ui/bandges/BadgeStatus/BadgeStatus"
import { ButtonSecondary } from "components/ui/buttons/ButtonSecondary/ButtonSecondary"
import { FileIcon } from "components/ui/icons/navbar/FileIcon"
import { Progress } from "components/ui/progress/Progress"
import { Dropdown } from "components/ui/dropdown/Dropdown"
import { CustomAuthorFilter } from "components/pages/PageAllFiles/layout/TableAllFiles/layout/CustomAuthorFilter/CustomAuthorFilter"
import { ButtonOptions } from "components/ui/buttons/ButtonOptions/ButtonOptions"
import { Spin } from "components/ui/spin/Spin"
import { Space } from "antd"
import { TableAllFilesHandlers } from "./TableAllFilesHandlers"
import { TableAllFilesUtils } from "./TableAllFilesUtils"
import { useUserProfile } from "hooks/api/user/user.hook"
import { formatDate, formatTime, parseFileSizeInMB } from "helpers/utils.helper"
import { DATE_FORMAT, ISO_DATE_FORMAT } from "helpers/dateFormat.helper"
import type { ColumnsType } from "antd/es/table"
import type { TTable } from "types/table.type"
import type { FilterDropdownProps } from "antd/es/table/interface"
import { Tooltip } from "components/ui/tooltip/Tooltip"
import { WarningOrangeIcon } from "components/ui/icons/Warning/WarningOrangeIcon"
import { CustomStatusFilter } from "./layout/CustomStatusFilter/CustomStatusFilter"
import { useFillesTableStore } from "store/table.store"
import style from "./TableAllFiles.module.scss"

const maxFileSizeMB = Number(import.meta.env.VITE_MAX_FILE_SIZE_MB) || 500

// Filters for Author column
const AUTHOR_FILTERS = [
	{
		text: "Me",
		value: "me",
	},
	{
		text: "Others",
		value: "other",
	},
]

const STATUS_FILTERS = [
	{
		text: "Uploaded",
		value: "uploaded",
	},
	{
		text: "Processing",
		value: "processing",
	},
	{
		text: "Processed",
		value: "processed",
	},
	{
		text: "In queue",
		value: "in_queue",
	},
	{
		text: "Failed",
		value: "failed",
	},
]

const { proccessButton, nameBlock, fileIcon, badgeStatus, statusBlock, WarningIcon, spinner } = style

export const TableAllFilesColumns = () => {
	const { startProcessingHandle } = TableAllFilesHandlers()
	const { data: userProfile } = useUserProfile(false)
	const { tableParams } = useFillesTableStore()
	const processingIds = useFillesTableStore((state) => state.processingIds)
	const duplicatingIds = useFillesTableStore((state) => state.duplicatingIds)
	const changingOwnerIds = useFillesTableStore((state) => state.changingOwnerIds)
	const { getDropDownItems } = TableAllFilesUtils()

	const columns: ColumnsType<TTable> = [
		{
			title: "NAME",
			dataIndex: "name",
			key: "name",
			width: 450,
			render: (item) => (
				<div className={nameBlock} data-testid="name">
					<FileIcon className={fileIcon} /> {item}
				</div>
			),
		},
		{
			title: "AUTHOR",
			dataIndex: "author",
			key: "author",
			render: (record) => (
				<div data-testid="author">
					{record.firstName} {record.lastName?.charAt(0)}.
				</div>
			),
			filters: AUTHOR_FILTERS,
			defaultFilteredValue:
				tableParams.filter.owner && tableParams.filter.owner != "all"
					? [tableParams.filter.owner]
					: tableParams.filter.user
						? [tableParams.filter.user]
						: null,
			filterMultiple: false,
			filterDropdown: (props: FilterDropdownProps) => (
				<CustomAuthorFilter
					{...props}
					filteredOptions={AUTHOR_FILTERS}
					selectedKeys={props.selectedKeys}
					clearFilters={props.clearFilters || (() => {})}
					confirm={() => props.confirm()}
					dataIndex="author"
					setSelectedKeys={props.setSelectedKeys}
				/>
			),
		},
		{
			title: "STATUS",
			dataIndex: "status",
			key: "status",
			render: (item) => (
				<div className={statusBlock} data-testid="status">
					<BandgeStatus type={item.status} className={badgeStatus} />
					{item.supportedStatus === "not_supported" ? (
						<Tooltip
							text="This file format is partially supported. The interactions and searches within the file will yield results with
					low accuracy and quality."
							placement="bottom"
						>
							<div style={{ width: "fit-content" }}>
								<WarningOrangeIcon className={WarningIcon} />
							</div>
						</Tooltip>
					) : null}
				</div>
			),
			filters: STATUS_FILTERS,
			filterMultiple: true,
			defaultFilteredValue: tableParams.filter.status,
			filterDropdown: (props: FilterDropdownProps) => (
				<CustomStatusFilter
					{...props}
					filteredOptions={STATUS_FILTERS}
					selectedKeys={props.selectedKeys}
					clearFilters={props.clearFilters || (() => {})}
					confirm={() => props.confirm()}
					dataIndex="status"
					setSelectedKeys={props.setSelectedKeys}
				/>
			),
		},
		{
			title: "UPLOADED",
			dataIndex: "uploadDate",
			key: "uploadDate",
			render: (item) => <div data-testid="upload-date">{formatDate(item, ISO_DATE_FORMAT, DATE_FORMAT)}</div>,
		},
		{
			title: "PROCESSED",
			dataIndex: "lastProcessingDate",
			key: "lastProcessingDate",
			render: (item) =>
				item.duration ? (
					<div data-testid="processed-date">
						<Tooltip text={`Processing duration: ${formatTime(item.duration)}`} placement="bottom">
							{formatDate(item.processedDate, ISO_DATE_FORMAT, DATE_FORMAT)}
						</Tooltip>
					</div>
				) : (
					<div data-testid="processed-date">{formatDate(item.processedDate, ISO_DATE_FORMAT, DATE_FORMAT)}</div>
				),
		},
		{
			title: "SIZE",
			dataIndex: "size",
			key: "size",
			render: (size) => {
				const currentFileSize = parseFileSizeInMB(size)
				if (currentFileSize > maxFileSizeMB) {
					return (
						<div data-testid="size">
							{size}{" "}
							<Tooltip
								text={`File size exceeds ${maxFileSizeMB}MB. This may cause failure when processing the file.`}
								placement="bottom"
							>
								<span>
									<WarningOrangeIcon className={WarningIcon} />
								</span>
							</Tooltip>
						</div>
					)
				} else {
					return <div data-testid="size">{size}</div>
				}
			},
		},
		{
			title: "",
			dataIndex: "processing",
			key: "processing",
			width: 300,
			render: (item) => {
				if (item.status === "uploaded" && item.owner === "me") {
					// Show loading spinner if the file is being processed
					// and the user is the owner of the file
					// Otherwise, show the "Start processing" button
					return processingIds.includes(item.id) ? (
						<div className={spinner} data-testid="processing">
							<Spin size="default" />
						</div>
					) : (
						<div data-testid="processing">
							<ButtonSecondary className={proccessButton} onClick={(e) => startProcessingHandle(item.id, e)}>
								Start processing
							</ButtonSecondary>
						</div>
					)
				} else if (item.status === "failed" && item.owner === "me") {
					// Show loading spinner if the file is being processed
					// and the user is the owner of the file
					// Otherwise, show the "Reprocess" button
					return processingIds.includes(item.id) ? (
						<div className={spinner} data-testid="processing">
							<Spin size="default" />
						</div>
					) : (
						<div data-testid="processing">
							<ButtonSecondary className={proccessButton} onClick={(e) => startProcessingHandle(item.id, e)}>
								Reprocess
							</ButtonSecondary>
						</div>
					)
				} else if (item.status === "processing") {
					return (
						<div data-testid="processing">
							<Progress progress={item.progress} />
						</div>
					)
				}
				return null
			},
		},
		{
			title: "",
			dataIndex: "action",
			key: "action",
			render: (item) => {
				if (item?.status != "processing" && item?.status != "in_queue") {
					if (duplicatingIds.includes(item.id)) {
						return (
							<div data-testid="file-duplication">
								<Spin size="default" />
							</div>
						)
					}

					if (changingOwnerIds.includes(item.id)) {
						return (
							<div data-testid="file-owner-change">
								<Spin size="default" />
							</div>
						)
					}

					return (
						<div onClick={(e) => e.stopPropagation()} data-testid="action-dropdown">
							<Dropdown items={getDropDownItems(item.id, item.status, item.fileName, item.owner, item.ownerId)}>
								<Space>
									<ButtonOptions />
								</Space>
							</Dropdown>
						</div>
					)
				}
				return null
			},
		},
	]

	// Remove author column for non-admin users
	const isAdmin = userProfile?.role === "Admin" || userProfile?.role === "Super Admin"
	const filteredColumns = isAdmin ? columns : columns.filter((column) => column.key !== "author")

	return filteredColumns
}
